// src/components/partners/RetailerList.jsx
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Row, Col, Form, InputGroup, Alert, Badge, Modal } from 'react-bootstrap';
import { Search, Edit, Trash, RefreshCw, XCircle, Info, Users } from 'lucide-react';
import { getUsersByRole } from '../../api/users';
import useAuth from '../../hooks/useAuth';
import { assignRetailerToCommercial, deleteMultipleRetailers, deleteRetailer, getAllRetailers, getRetailersByCommercial } from '../../api/partner';

const RetailerList = ({ onAddRetailer, onEditRetailer, onViewRetailer, onAssignCommercial, commercialId }) => {
    const { isAdmin, isManager, isSalesRep, isDistributor, user } = useAuth();
    const [retailers, setRetailers] = useState([]);
    const [filteredRetailers, setFilteredRetailers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedRetailers, setSelectedRetailers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [retailerToAssign, setRetailerToAssign] = useState(null);
    const [selectedCommercialId, setSelectedCommercialId] = useState('');
    const [commercials, setCommercials] = useState([]);
    const [loadingCommercials, setLoadingCommercials] = useState(false);

    const canModify = isAdmin || isManager || isSalesRep;
    const canAssign = isAdmin || isManager;

    useEffect(() => {
        fetchRetailers();
        if (canAssign) {
            fetchCommercials();
        }
    }, [refreshTrigger, commercialId]);

    useEffect(() => {
        filterRetailers();
    }, [searchTerm, retailers]);

    useEffect(() => {
        // Réinitialiser la sélection quand les détaillants filtrés changent
        setSelectedRetailers([]);
        setSelectAll(false);
    }, [filteredRetailers]);

    const fetchRetailers = async () => {
        try {
            setLoading(true);
            setError('');

            let data;
            if (isSalesRep) {
                // Si un commercial est spécifié, ne charger que ses détaillants
                data = await getRetailersByCommercial();
            } else {
                // Sinon, charger tous les détaillants
                data = await getAllRetailers();
            }

            setRetailers(Array.isArray(data) ? data : []);
            setFilteredRetailers(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Impossible de charger les détaillants. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCommercials = async () => {
        try {
            setLoadingCommercials(true);
            const data = await getUsersByRole('ROLE_SALES_REP');
            setCommercials(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Erreur lors du chargement des commerciaux', err);
        } finally {
            setLoadingCommercials(false);
        }
    };

    const filterRetailers = () => {
        if (searchTerm.trim() === '') {
            setFilteredRetailers(Array.isArray(retailers) ? retailers : []);
        } else {
            const filtered = retailers.filter(retailer =>
                retailer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (retailer.email && retailer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (retailer.businessType && retailer.businessType.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (retailer.commercialUser &&
                    ((retailer.commercialUser.firstName && retailer.commercialUser.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                        (retailer.commercialUser.lastName && retailer.commercialUser.lastName.toLowerCase().includes(searchTerm.toLowerCase()))))
            );
            setFilteredRetailers(Array.isArray(filtered) ? filtered : []);
        }
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
        setSuccess('');
        setError('');
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce détaillant ?')) {
            try {
                await deleteRetailer(id);
                setRetailers(retailers.filter(retailer => retailer.id !== id));
                setSuccess('Détaillant supprimé avec succès');
            } catch (err) {
                setError('Échec de la suppression du détaillant. Veuillez réessayer.');
                console.error(err);
            }
        }
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);
        setSelectedRetailers(isChecked ? filteredRetailers.map(retailer => retailer.id) : []);
    };

    const handleSelectRetailer = (id) => {
        setSelectedRetailers(prev => {
            if (prev.includes(id)) {
                return prev.filter(retailerId => retailerId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleDeleteSelected = async () => {
        if (selectedRetailers.length === 0) return;

        if (window.confirm(`Êtes-vous sûr de vouloir supprimer les ${selectedRetailers.length} détaillants sélectionnés ?`)) {
            try {
                await deleteMultipleRetailers(selectedRetailers);
                setRetailers(retailers.filter(retailer => !selectedRetailers.includes(retailer.id)));
                setSelectedRetailers([]);
                setSelectAll(false);
                setSuccess(`${selectedRetailers.length} détaillants supprimés avec succès`);
            } catch (err) {
                setError('Échec de la suppression des détaillants sélectionnés. Veuillez réessayer.');
                console.error(err);
            }
        }
    };

    const openAssignModal = (retailer) => {
        setRetailerToAssign(retailer);
        setSelectedCommercialId(retailer.commercialUser ? retailer.commercialUser.id : '');
        setShowAssignModal(true);
    };

    const handleAssignCommercial = async () => {
        if (!retailerToAssign) return;

        try {
            await assignRetailerToCommercial(retailerToAssign.id, selectedCommercialId);
            setShowAssignModal(false);
            setSuccess('Commercial assigné avec succès');
            handleRefresh();
        } catch (err) {
            setError('Échec de l\'assignation du commercial. Veuillez réessayer.');
            console.error(err);
        }
    };

    const getCommercialName = (commercial) => {
        if (!commercial) return '-';
        return commercial.fullName || '';
    };

    // Badge TVA réutilisé (tableau + cartes)
    const renderVatBadge = (retailer) =>
        retailer.vatApplicable ? (
            <Badge bg="success">Oui</Badge>
        ) : (
            <Badge bg="secondary">Non</Badge>
        );

    // Groupe d'actions réutilisé (tableau lg + cartes mobile)
    const renderActions = (retailer) => (
        <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button
                variant="outline-info"
                size="sm"
                onClick={() => onViewRetailer(retailer.id)}
                title="Détails"
            >
                <Info size={16} />
            </Button>

            {canAssign && (
                <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => openAssignModal(retailer)}
                    title="Assigner un commercial"
                >
                    <Users size={16} />
                </Button>
            )}

            {canModify && (
                <>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onEditRetailer(retailer.id)}
                        title="Modifier"
                    >
                        <Edit size={16} />
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(retailer.id)}
                        title="Supprimer"
                    >
                        <Trash size={16} />
                    </Button>
                </>
            )}
        </div>
    );

    return (
        <>
            <Card className="shadow-sm">
                <Card.Header>
                    <Row className="align-items-center">
                        <Col>
                            <h5 className="mb-0">
                                {isSalesRep
                                    ? 'Mes clients détaillants'
                                    : 'Liste des détaillants'}
                            </h5>
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRefresh}
                                title="Rafraîchir"
                            >
                                <RefreshCw size={16} />
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Row className="mb-3 g-2">
                        <Col xs={12} md={8}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Rechercher par nom, email, type d'activité ou commercial..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col xs={12} md={4}>
                            {canModify && selectedRetailers.length > 0 && (
                                <Button
                                    variant="danger"
                                    onClick={handleDeleteSelected}
                                    className="w-100"
                                >
                                    <XCircle size={16} className="me-1" />
                                    Supprimer sélectionnés ({selectedRetailers.length})
                                </Button>
                            )}
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2">Chargement des détaillants...</p>
                        </div>
                    ) : filteredRetailers.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            Aucun détaillant trouvé
                        </div>
                    ) : (
                        <>
                            {/* ≥ lg : tableau */}
                            <div className="table-responsive d-none d-lg-block">
                                <Table striped hover className="mb-0">
                                    <thead>
                                        <tr>
                                            {canModify && (
                                                <th width="50">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectAll}
                                                        onChange={handleSelectAll}
                                                    />
                                                </th>
                                            )}
                                            <th>Nom</th>
                                            <th>Email</th>
                                            <th>Type d'activité</th>
                                            <th>Commercial</th>
                                            <th>TVA</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRetailers.map(retailer => (
                                            <tr key={retailer.id}>
                                                {canModify && (
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selectedRetailers.includes(retailer.id)}
                                                            onChange={() => handleSelectRetailer(retailer.id)}
                                                        />
                                                    </td>
                                                )}
                                                <td>{retailer.name}</td>
                                                <td>{retailer.email || '-'}</td>
                                                <td>{retailer.businessType || '-'}</td>
                                                <td>{getCommercialName(retailer.commercialUser)}</td>
                                                <td>{renderVatBadge(retailer)}</td>
                                                <td>{renderActions(retailer)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* < lg : cartes */}
                            <div className="d-lg-none">
                                {canModify && (
                                    <Form.Check
                                        type="checkbox"
                                        label="Tout sélectionner"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                        className="mb-2"
                                    />
                                )}
                                {filteredRetailers.map(retailer => (
                                    <div key={retailer.id} className="border rounded p-3 mb-2">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="d-flex align-items-start gap-2">
                                                {canModify && (
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectedRetailers.includes(retailer.id)}
                                                        onChange={() => handleSelectRetailer(retailer.id)}
                                                        className="mt-1"
                                                    />
                                                )}
                                                <div>
                                                    <div className="fw-bold">{retailer.name}</div>
                                                    <small className="text-muted text-break">{retailer.email || '-'}</small>
                                                </div>
                                            </div>
                                            <div className="text-nowrap">
                                                <span className="text-muted small me-1">TVA</span>
                                                {renderVatBadge(retailer)}
                                            </div>
                                        </div>

                                        <div className="small mb-1">
                                            <span className="text-muted">Type d'activité : </span>
                                            {retailer.businessType || '-'}
                                        </div>
                                        <div className="small mb-3">
                                            <span className="text-muted">Commercial : </span>
                                            {getCommercialName(retailer.commercialUser)}
                                        </div>

                                        {renderActions(retailer)}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal d'assignation de commercial */}
            <Modal
                show={showAssignModal}
                onHide={() => setShowAssignModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Assigner un commercial</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingCommercials ? (
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <p className="mt-2">Chargement des commerciaux...</p>
                        </div>
                    ) : (
                        <>
                            <p>
                                <strong>Détaillant :</strong> {retailerToAssign?.name}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Commercial</Form.Label>
                                <Form.Select
                                    name="commercialUser"
                                    value={selectedCommercialId}
                                    onChange={(e) => setSelectedCommercialId(e.target.value)}
                                >
                                    <option value="">-- Aucun commercial--</option>
                                    {commercials?.map(commercial => (
                                        <option key={commercial.id} value={commercial.id}>
                                            {commercial.fullName}
                                        </option>
                                    ))}
                                </Form.Select>

                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
                        Annuler
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAssignCommercial}
                        disabled={loadingCommercials}
                    >
                        Assigner
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RetailerList;